import { useState } from 'react';
import { Coins, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAiCreditPacks } from '@/hooks/queries/useAiCredits';
import { AiCreditsPurchaseSheet } from './AiCreditsPurchaseSheet';

interface ChatInlineRechargeProps {
  onPurchaseComplete?: () => void;
}

function formatFCFA(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ChatInlineRecharge({ onPurchaseComplete }: ChatInlineRechargeProps) {
  const { data: packs } = useAiCreditPacks();
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  function handlePackSelect(credits: number) {
    setSelectedCredits(credits);
    setSheetOpen(true);
  }

  return (
    <>
      <div className="mx-auto my-3 max-w-[340px] rounded-xl border border-charcoal-950 bg-white dark:bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex size-7 items-center justify-center rounded-full bg-warning-light">
            <Coins className="size-3.5 text-warning-dark" />
          </div>
          <div>
            <p className="text-sm font-semibold text-charcoal-900 dark:text-charcoal-100">
              Crédits épuisés
            </p>
            <p className="text-[11px] text-neutral-500">
              Rechargez pour continuer la conversation.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {(packs ?? []).map((pack) => (
            <button
              key={pack.id}
              type="button"
              onClick={() => handlePackSelect(pack.credits)}
              className={cn(
                'rounded-lg border-2 border-neutral-200 dark:border-charcoal-700 p-2 text-center transition-all',
                'hover:border-lime-400 active:scale-[0.97] active:border-lime-500',
              )}
            >
              <p className="font-heading text-base font-bold text-charcoal-900 dark:text-charcoal-100">
                {pack.credits}
              </p>
              <p className="text-[10px] text-neutral-500">crédits</p>
              <p className="mt-0.5 text-xs font-semibold text-charcoal-700 dark:text-charcoal-300">
                {formatFCFA(pack.price)} F
              </p>
            </button>
          ))}
        </div>

        <Button
          type="button"
          variant="cta"
          size="sm"
          className="mt-3 w-full"
          onClick={() => {
            setSelectedCredits(null);
            setSheetOpen(true);
          }}
        >
          <Zap className="size-3.5" />
          Recharger
        </Button>
      </div>

      <AiCreditsPurchaseSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        quick
        preselectedCredits={selectedCredits ?? undefined}
        onPurchaseComplete={onPurchaseComplete}
      />
    </>
  );
}
